import type { FC } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Animated from 'react-native-reanimated';
import type ApiLoyaltyCard from 'src/core/models/api/ApiLoyaltyCard';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import { useLoyaltyStore } from 'src/domain/stores/useLoyaltyStore';
import { TertiaryButton } from 'src/presentation/components/molecules/Button';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import BenefitResortPicker from 'src/presentation/components/organisms/BenefitResortPicker';
import BottomSheet from 'src/presentation/components/organisms/BottomSheet';
import { HtmlView } from 'src/presentation/components/organisms/HtmlView';
import LeaveAppDialog from 'src/presentation/dialogs/LeaveAppDialog';
import { useBenefitCta } from 'src/presentation/hooks/useBenefitCta';
import { useCrossFade } from 'src/presentation/hooks/useCrossFade';

const BENEFIT_STEP = 'benefit';
const RESORTS_STEP = 'resorts';

interface SheetProps {
	benefit: ApiLoyaltyCard | null;
	resortPickerCard: ApiLoyaltyCard | null;
	onClose: () => void;
	onBack: () => void;
}

const BenefitDetail: FC<{ card: ApiLoyaltyCard }> = ({ card }: { card: ApiLoyaltyCard }) => {
	const { editorialLabel, redeem } = useBenefitCta(card);

	const isUsed = card.used === true;
	const html = card.long_description && card.long_description.length > 1 ? card.long_description : card.description;

	return (
		<>
			{card.image_thumb ? (
				<View style={styles.media}>
					<ThermenImage contentFit="cover" source={{ uri: card.image_thumb }} style={styles.poster} />
				</View>
			) : null}
			<View style={styles.body}>
				<Text style={styles.title}>{card.name}</Text>
				<HtmlView html={html} />
			</View>
			{card.no_cta === true ? null : (
				<TertiaryButton
					buttonStyle={styles.cta}
					disabled={isUsed}
					label={isUsed ? Translations.loyaltyYouUsedThisBenefit : (editorialLabel ?? Translations.loyaltyUseThisBenefit)}
					onPress={redeem}
				/>
			)}
		</>
	);
};

// One sheet for both steps: swapping the content keeps a single native modal on screen, where
// dismissing one and presenting another in the same commit leaves iOS with nothing to present on.
const BenefitSheet: FC<SheetProps> = ({ benefit, resortPickerCard, onClose, onBack }: SheetProps) => {
	const { displayedKey, style } = useCrossFade(resortPickerCard === null ? BENEFIT_STEP : RESORTS_STEP);

	const showsResorts = displayedKey === RESORTS_STEP && resortPickerCard !== null;
	// A resort list the grid card opened on its own has no benefit behind it to go back to.
	const stepsBack = resortPickerCard !== null && benefit !== null;

	return (
		<BottomSheet contentKey={displayedKey} visible={true} onClose={onClose} {...(stepsBack ? { onBack } : {})}>
			<Animated.View style={[styles.step, style]}>
				{showsResorts ? (
					<BenefitResortPicker card={resortPickerCard} onNavigate={onClose} />
				) : benefit === null ? null : (
					<BenefitDetail card={benefit} />
				)}
			</Animated.View>

			{/* iOS refuses a second modal over a modal, so the confirmation lives in this sheet's own tree. */}
			<LeaveAppDialog />
		</BottomSheet>
	);
};

const BenefitDialog: FC = () => {
	const { selectedBenefit, resortPickerCard, closeBenefit, closeResortPicker } = useLoyaltyStore();

	if (selectedBenefit === null && resortPickerCard === null) {
		return null;
	}

	return (
		<BenefitSheet
			benefit={selectedBenefit}
			resortPickerCard={resortPickerCard}
			onBack={closeResortPicker}
			onClose={closeBenefit}
		/>
	);
};

const styles = StyleSheet.create({
	step: {
		gap: Dimens.gap14,
	},
	media: {
		aspectRatio: Dimens.aspectRatio2By1,
		borderRadius: Dimens.borderRadius4,
		backgroundColor: Colors.gray100,
		overflow: 'hidden',
	},
	poster: {
		width: '100%',
		height: '100%',
	},
	body: {
		gap: Dimens.gap10,
	},
	title: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize22,
		lineHeight: Dimens.lineHeight30,
		color: Colors.gray900,
	},
	cta: {
		height: Dimens.buttonHeight48,
	},
});

export default BenefitDialog;
